import { Logger } from 'src/utils/Logger';

const INVITE_EMAIL_KEY = 'afternoon_invite_email';
const INVITE_TENANT_KEY = 'afternoon_invite_tenant';
const INVITE_TIMESTAMP_KEY = 'afternoon_invite_timestamp';
const TTL_MS = 5 * 60 * 1000; // 5 minutes

interface InviteData {
  email: string;
  tenantName: string;
}

export const setInviteData = (email: string, tenantName: string): void => {
  try {
    sessionStorage.setItem(INVITE_EMAIL_KEY, email);
    sessionStorage.setItem(INVITE_TENANT_KEY, tenantName);
    sessionStorage.setItem(INVITE_TIMESTAMP_KEY, Date.now().toString());
  } catch (e) {
    Logger.error('sessionStorage unavailable for invite data', e);
  }
};

export const getInviteData = (): InviteData | null => {
  try {
    const email = sessionStorage.getItem(INVITE_EMAIL_KEY);
    const tenantName = sessionStorage.getItem(INVITE_TENANT_KEY);
    const timestamp = sessionStorage.getItem(INVITE_TIMESTAMP_KEY);

    if (!email || !tenantName || !timestamp) return null;

    if (Date.now() - parseInt(timestamp, 10) > TTL_MS) {
      clearInviteData();
      return null;
    }
    return { email, tenantName };
  } catch {
    return null;
  }
};

export const clearInviteData = (): void => {
  try {
    sessionStorage.removeItem(INVITE_EMAIL_KEY);
    sessionStorage.removeItem(INVITE_TENANT_KEY);
    sessionStorage.removeItem(INVITE_TIMESTAMP_KEY);
  } catch (e) {
    Logger.error('sessionStorage unavailable for clearing invite data', e);
  }
};

const ACCEPT_LINK_TENANT_KEY = 'afternoon_accept_link_tenant';
const ACCEPT_LINK_TOKEN_KEY = 'afternoon_accept_link_token';
const ACCEPT_LINK_TIMESTAMP_KEY = 'afternoon_accept_link_timestamp';

// The invitee leaves our origin for Ory and comes back on a dead-end page that carries no
// invite details of its own, so the accept-link they arrived with is kept for the length of
// that round trip - long enough to cover a slow SSO detour, short enough that a shared
// machine does not offer a stranger's invite a resend.
const ACCEPT_LINK_TTL_MS = 30 * 60 * 1000;

export interface AcceptLinkRef {
  tenantId: string;
  token: string;
}

export const setAcceptLinkRef = ({ tenantId, token }: AcceptLinkRef): void => {
  try {
    sessionStorage.setItem(ACCEPT_LINK_TENANT_KEY, tenantId);
    sessionStorage.setItem(ACCEPT_LINK_TOKEN_KEY, token);
    sessionStorage.setItem(ACCEPT_LINK_TIMESTAMP_KEY, Date.now().toString());
  } catch (e) {
    Logger.error('sessionStorage unavailable for accept-link reference', e);
  }
};

export const getAcceptLinkRef = (): AcceptLinkRef | null => {
  try {
    const tenantId = sessionStorage.getItem(ACCEPT_LINK_TENANT_KEY);
    const token = sessionStorage.getItem(ACCEPT_LINK_TOKEN_KEY);
    const timestamp = sessionStorage.getItem(ACCEPT_LINK_TIMESTAMP_KEY);

    if (!tenantId || !token || !timestamp) return null;

    if (Date.now() - parseInt(timestamp, 10) > ACCEPT_LINK_TTL_MS) {
      clearAcceptLinkRef();
      return null;
    }
    return { tenantId, token };
  } catch {
    return null;
  }
};

export const clearAcceptLinkRef = (): void => {
  try {
    sessionStorage.removeItem(ACCEPT_LINK_TENANT_KEY);
    sessionStorage.removeItem(ACCEPT_LINK_TOKEN_KEY);
    sessionStorage.removeItem(ACCEPT_LINK_TIMESTAMP_KEY);
  } catch (e) {
    Logger.error('sessionStorage unavailable for clearing accept-link reference', e);
  }
};
